// Step 1 of the missing-item flow: prove the picker is at the right shelf.
//
// The count itself is only worth anything if it was taken at the location the
// wave sent them to, so nothing else is on screen until that barcode is
// scanned — no count list, no Done button. Until 2026-08-18 this phase
// rendered the full count list with a small "SCAN LOCATION" line beneath it,
// and pickers read the list as "the count has started", missed the gate
// entirely, and reported the flow as skipping the location scan.
//
// Shape matches every other scan-first resting state in the app
// (ScanPromptHero: muted glyph + full-size command words), with the location
// the picker must scan and the item under investigation stated underneath.
import { MapPin, PackageSearch } from "lucide-react";
import ScanPromptHero from "../picking-shared/ScanPromptHero";
import { useTranslation } from "../../i18n";

interface Props {
    /** Barcode the picker must scan (the line's location). */
    location: string;
    /** Human-readable name when the BE resolved one; falls back to the code. */
    locationDisplayName?: string;
    /** SKU that sent them here. */
    sku: string;
    /** Units still outstanding on the line. */
    qtyOutstanding: number;
    /** True while the bad-scan overlay is up — hides the hero behind it. */
    flashHidden?: boolean;
    busy?: boolean;
    onCancel: () => void;
}

const MissingLocationGate = ({
    location, locationDisplayName, sku, qtyOutstanding, flashHidden, busy, onCancel,
}: Props) => {
    const { t } = useTranslation();
    return (
        <div className={"picking-missing-gate" + (flashHidden ? " scan-flash-hidden" : "")}>
            <ScanPromptHero prompt={t("picking_missing_cmd_scan_loc")}>
                <div className="picking-missing-gate-target">
                    {/* The barcode to scan, verbatim — the picker is matching
                        the label in front of them against this. */}
                    <div className="picking-missing-gate-loc">
                        <MapPin size={20} />
                        <span className="picking-missing-gate-loc-code">
                            {locationDisplayName || location}
                        </span>
                    </div>
                    <div className="picking-missing-gate-sku">
                        <PackageSearch size={16} />
                        {t("picking_missing_gate_looking_for", {
                            count: String(qtyOutstanding),
                            sku,
                        })}
                    </div>
                    <div className="picking-missing-gate-why">
                        {t("picking_missing_gate_why")}
                    </div>
                </div>
            </ScanPromptHero>
            <div className="picking-missing-actions">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onCancel}
                    disabled={busy}
                >
                    {t("picking_missing_cancel")}
                </button>
            </div>
        </div>
    );
};

export default MissingLocationGate;
